import { Icon, Tag, useTranslate } from '@athena/ui'
import type { ReactElement } from 'react'

import { planName, roleName, type Role } from '../../lib/display.js'

/*
 * Three families of tag, kept apart by shape before colour.
 *
 * - **Profile** — what an account is: Tutor, Student, Administrator. An
 *   outlined square with an icon, and no colour of its own. Identity is
 *   information; nothing about it needs to stand out.
 * - **Status** — what state it is in: Active, Disabled, Invitation pending.
 *   A pill with a glyph. The one family that uses tone, because a disabled
 *   account is the thing an administrator scans a list for.
 * - **Plan** — which plan applies: Free, Premium, Premium Plus. A filled
 *   square; a paid plan in the brand's indigo, Free in neutral.
 *
 * So a row can carry all three and read as three kinds of fact, and a
 * greyscale screenshot still tells them apart.
 */

const ROLE_ICONS: Record<Role, ReactElement> = {
  TUTOR: <Icon.Presentation />,
  STUDENT: <Icon.GraduationCap />,
  ADMIN: <Icon.ShieldCheck />,
}

export function RoleTag({ role }: { role: Role }) {
  const t = useTranslate()
  return (
    <Tag shape="square" fill="outline" icon={ROLE_ICONS[role]}>
      {roleName(t, role)}
    </Tag>
  )
}

export function AccountStatusTag({ disabled }: { disabled: boolean }) {
  const t = useTranslate()
  return disabled ? (
    <Tag tone="muted" icon="⊘">
      {t('admin.statusDisabled')}
    </Tag>
  ) : (
    <Tag tone="positive" icon="●">
      {t('admin.statusActive')}
    </Tag>
  )
}

/** A student account that exists and has not yet joined through a tutor's invitation. */
export function InvitationTag() {
  const t = useTranslate()
  return (
    <Tag tone="brand" icon="◌">
      {t('admin.statusInvited')}
    </Tag>
  )
}

export function PlanTag({ code }: { code: string }) {
  const t = useTranslate()
  return (
    <Tag shape="square" tone={code === 'FREE' ? 'neutral' : 'brand'}>
      {planName(t, code)}
    </Tag>
  )
}

export function SubjectStatusTag({ active }: { active: boolean }) {
  const t = useTranslate()
  return active ? (
    <Tag tone="positive" icon="●">
      {t('admin.subjectStatusActive')}
    </Tag>
  ) : (
    <Tag tone="muted" icon="⊘">
      {t('admin.subjectStatusInactive')}
    </Tag>
  )
}
